/**
 * @fileoverview Member Snapshot Helper Utilities
 *
 * This module provides utility functions for member snapshot ribbon components,
 * including color management, member status determination, and benefit data generation.
 *
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { baseVariables } from '@marriott/mi-ui-library';
import { getColorByCode, getMemberLevelPropertiesByCode, getMemberLevelV2, getGradientByCode } from './helper';
import { replaceText } from './promotionHelper';
import {
  MEMBER_STATUS,
  BENEFIT_TEMPLATES,
  MEMBER_NIGHT_RANGES,
  MEMBERSHIP_CODES,
  CORE_LEVEL_CODES,
  BENEFIT_ROTATION,
  constants,
  OVERVIEW_CONSTANT,
  LOCATION_RESTRICTIONS,
  EARNINGS_SUMMARY_CONSTANTS,
} from './constants/constants';
import {
  AemModel,
  BenefitData,
  MemberBenefitResult,
} from '../../organisms/MemberSnapshotRibbon/MemberSnapshotRibbonV2.types';
import { getLifeTimeCodes } from './overviewHelper';
import { membersnapshotribbonHelper } from './overviewHelper';
import {
  MessageTemplate,
  ModelTypes,
  UserDataTypes,
  UserLevelDataTypes,
} from '../../organisms/MemberSnapshotRibbon/MemberStatusCard/MemberBenefits/MemberBenefits.types';
import type { BenefitConfig } from './memberSnapshotHelper.types';

/**
 * Extracts icon class name from AEM icon path
 * Converts paths like 'marriott-account:general/icon-wifi' to 'icon-wifi'
 *
 * @param iconPath - The full AEM icon path
 * @returns Clean icon class name
 */
export const extractIconClass = (iconPath: string | undefined): string => {
  if (!iconPath) return '';

  // Handle different AEM icon path formats
  if (iconPath.includes(':')) {
    // Format: 'marriott-account:general/icon-wifi' -> 'icon-wifi'
    const parts = iconPath.split(':');
    if (parts.length > 1) {
      const iconPart = parts[1];
      if (iconPart.includes('/')) {
        return iconPart.split('/').pop() || '';
      }
      return iconPart;
    }
  }

  // If it's already a simple icon name, return as is
  if (iconPath.startsWith('icon-')) {
    return iconPath;
  }

  // Return empty string if no valid icon found
  return '';
};

export const buildMemberBenefitsMessagesWithLifetime = (
  model: ModelTypes,
  customerData: UserDataTypes,
  memberStatusList: UserLevelDataTypes,
  isNightsModel = false
) => {
  const rewards = customerData?.loyaltyInformation?.rewards;
  const lifetimeCode = rewards?.eliteLifetimeLevel?.code || '';
  const levelCode = rewards?.level?.code || '';
  const levelTypeCode = rewards?.levelType?.code || '';

  const coreLevelCode = getMemberLevelV2(levelCode || '', levelTypeCode || '');
  const lifetimeLevelCode = getLifeTimeCodes(lifetimeCode || '');
  const isLifetimeUserWithMatchingTier = coreLevelCode === lifetimeLevelCode;

  const msgs = membersnapshotribbonHelper(model, customerData, memberStatusList, isNightsModel);

  if (!isLifetimeUserWithMatchingTier) return msgs;

  // Filter out renew message(s) for lifetime user with matching tier
  const renewIcon = OVERVIEW_CONSTANT.MEMBER_LEVEL_MSG_ICONS.renew;
  return (msgs || []).filter((m: string | MessageTemplate) => (typeof m === 'string' ? true : m?.icon !== renewIcon));
};

/**
 * Normalizes tier codes to core codes for consistent handling across all environments
 * Handles all tier variants: R/MRD/MGD -> R, S/SAR/SAC/SLR -> S, etc.
 *
 * @param levelTypeCode - The level type code to normalize
 * @returns Normalized core code (R, S, G, P, PPC, PFS)
 */
export const normalizeTierCode = (levelTypeCode: string | undefined): string => {
  if (!levelTypeCode) return CORE_LEVEL_CODES[0]; // Default to 'R'

  const upperCode = levelTypeCode.toUpperCase();

  // Check each membership code array and return the corresponding core code
  if (MEMBERSHIP_CODES.MEMBER_CODES.includes(upperCode)) {
    return CORE_LEVEL_CODES[0]; // 'R'
  }
  if (MEMBERSHIP_CODES.SLIVER_MEMBER_CODES.includes(upperCode)) {
    return CORE_LEVEL_CODES[1]; // 'S'
  }
  if (MEMBERSHIP_CODES.GOLD_MEMBER_CODES.includes(upperCode)) {
    return CORE_LEVEL_CODES[2]; // 'G'
  }
  if (MEMBERSHIP_CODES.PLATINUM_MEMBER_CODES.includes(upperCode)) {
    return CORE_LEVEL_CODES[3]; // 'P'
  }
  if (MEMBERSHIP_CODES.TITANIUM_MEMBER_CODES.includes(upperCode)) {
    return constants.TITANIUM_LEVEL_CODE; // 'PPC'
  }
  if (MEMBERSHIP_CODES.AMBASSADOR_MEMBER_CODES.includes(upperCode)) {
    return CORE_LEVEL_CODES[5]; // 'PFS'
  }

  // If it's already a core code, return it
  if (CORE_LEVEL_CODES.includes(upperCode) || upperCode === constants.TITANIUM_LEVEL_CODE) {
    return upperCode;
  }

  // Fallback to basic member
  return CORE_LEVEL_CODES[0]; // 'R'
};

/**
 * Gets the appropriate color for nights progress based on member tier
 *
 * @param levelTypeCode - The member's level type code (R, S, G, P, PPC, PFS)
 * @param memberStatusList - Optional array of member status configurations
 * @returns Object containing colorCode, arcColor, and textColor
 *
 * @example
 * ```typescript
 * const colors = getNightsProgressColors('G', memberStatusList);
 * console.log(colors.arcColor); // Returns the gold color for Gold Elite
 * ```
 */
export const getNightsProgressColors = (
  levelTypeCode: string | undefined,
  memberStatusList?: Array<{ id: string; label: string }>,
  isMemberRibbon?: boolean
) => {
  const colorCode = levelTypeCode;
  // Default fallback to Member orange color instead of black
  const memberDefaultColor =
    baseVariables.color.memberSolid || baseVariables.color.accent10 || baseVariables.color.base10;
  let arcColor = memberDefaultColor;

  if (colorCode && memberStatusList) {
    // Use the existing getMemberLevel function to properly map tier codes
    const mappedCode = getMemberLevelV2(undefined, colorCode) || colorCode;
    if (isMemberRibbon) {
      arcColor = getGradientByCode(colorCode || '', false) ?? memberDefaultColor;
    } else {
      const memberProperties = getMemberLevelPropertiesByCode(mappedCode, memberStatusList);
      arcColor = memberProperties.borderLeft || memberDefaultColor;
    }
  } else {
    // Check if it's a specific tier (not R/Member)
    if (levelTypeCode && levelTypeCode !== CORE_LEVEL_CODES[0]) {
      // For specific tiers (S, G, P, PPC, PFS), get their colors
      if (isMemberRibbon) {
        arcColor = getGradientByCode(colorCode || '', false) ?? memberDefaultColor;
      } else {
        const rawColor = getColorByCode(colorCode || '', false);
        arcColor = rawColor?.replace(/!important/g, '').replace(/;+$/, '') || memberDefaultColor;
      }
    }
    // For R tier or undefined, arcColor already defaults to memberDefaultColor
  }

  return {
    colorCode,
    arcColor,
    textColor: arcColor,
  };
};

/**
 * Determines member status based on nights stayed and spend
 *
 * @param levelTypeCode - The member's level type code
 * @param totalNights - Total nights stayed this year
 * @param totalSpend - Total spend this year
 * @returns Member status: 'not-near', 'near-renewing', or 'near-achieving'
 *
 * @example
 * ```typescript
 * const status = getMemberStatus('G', 45, 15000);
 * console.log(status); // 'near-achieving' for Gold member with 45 nights
 * ```
 */
export const getMemberStatus = (
  levelTypeCode: string | undefined,
  totalNights: number,
  totalSpend: number,
  lifetimeStatus?: string | null
): 'not-near' | 'near-renewing' | 'near-achieving' => {
  const nights = totalNights || 0;
  const spend = totalSpend || 0;

  // Normalize levelTypeCode to core code (handles all tier variants)
  const normalizedLevelTypeCode = normalizeTierCode(levelTypeCode);

  // Helper to check near renewing with lifetime status override
  const checkNearRenewing = (): 'not-near' | 'near-renewing' => {
    // If lifetime status matches current tier, show "not-near" instead of "near-renewing"
    // Per AC: "When a user's lifetime status = their current elite tier status, don't show stretch messaging when they are NEAR RENEWING"
    if (lifetimeStatus && levelTypeCode && lifetimeStatus === levelTypeCode) {
      return MEMBER_STATUS.NOT_NEAR;
    }
    return MEMBER_STATUS.NEAR_RENEWING;
  };

  switch (normalizedLevelTypeCode) {
    case CORE_LEVEL_CODES[0]: // 'R' - Basic
      if (
        nights >= MEMBER_NIGHT_RANGES.BASIC_NEAR_ACHIEVING.min &&
        nights <= MEMBER_NIGHT_RANGES.BASIC_NEAR_ACHIEVING.max
      ) {
        return MEMBER_STATUS.NEAR_ACHIEVING;
      }
      return MEMBER_STATUS.NOT_NEAR;

    case CORE_LEVEL_CODES[1]: // 'S' - Silver
      if (
        nights >= MEMBER_NIGHT_RANGES.SILVER_NEAR_RENEWING.min &&
        nights <= MEMBER_NIGHT_RANGES.SILVER_NEAR_RENEWING.max
      ) {
        return checkNearRenewing();
      }
      if (
        nights >= MEMBER_NIGHT_RANGES.SILVER_NEAR_ACHIEVING.min &&
        nights <= MEMBER_NIGHT_RANGES.SILVER_NEAR_ACHIEVING.max
      ) {
        return MEMBER_STATUS.NEAR_ACHIEVING;
      }
      return MEMBER_STATUS.NOT_NEAR;

    case CORE_LEVEL_CODES[2]: // 'G' - Gold
      if (
        nights >= MEMBER_NIGHT_RANGES.GOLD_NEAR_RENEWING.min &&
        nights <= MEMBER_NIGHT_RANGES.GOLD_NEAR_RENEWING.max
      ) {
        return checkNearRenewing();
      }
      if (
        nights >= MEMBER_NIGHT_RANGES.GOLD_NEAR_ACHIEVING.min &&
        nights <= MEMBER_NIGHT_RANGES.GOLD_NEAR_ACHIEVING.max
      ) {
        return MEMBER_STATUS.NEAR_ACHIEVING;
      }
      return MEMBER_STATUS.NOT_NEAR;

    case CORE_LEVEL_CODES[3]: // 'P' - Platinum
      if (
        nights >= MEMBER_NIGHT_RANGES.PLATINUM_NEAR_RENEWING.min &&
        nights <= MEMBER_NIGHT_RANGES.PLATINUM_NEAR_RENEWING.max
      ) {
        return checkNearRenewing();
      }
      if (
        nights >= MEMBER_NIGHT_RANGES.PLATINUM_NEAR_ACHIEVING.min &&
        nights <= MEMBER_NIGHT_RANGES.PLATINUM_NEAR_ACHIEVING.max
      ) {
        return MEMBER_STATUS.NEAR_ACHIEVING;
      }
      return MEMBER_STATUS.NOT_NEAR;

    case constants.TITANIUM_LEVEL_CODE: // 'PPC' - Titanium
      if (
        nights >= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_RENEWING.min &&
        nights <= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_RENEWING.max
      ) {
        return checkNearRenewing();
      }
      if (
        nights >= constants.AMBASSADOR_NIGHT_THRESHOLD ||
        (nights >= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.min &&
          nights <= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.max &&
          spend >= 23000) ||
        (nights >= 100 && spend >= 22000) ||
        (nights >= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.min &&
          nights <= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.max &&
          spend >= 22000)
      ) {
        return MEMBER_STATUS.NEAR_ACHIEVING;
      }
      return MEMBER_STATUS.NOT_NEAR;

    case CORE_LEVEL_CODES[5]: // 'PFS' - Ambassador
    case OVERVIEW_CONSTANT.AMBASSADOR_CODE[1]: // 'PFR' - Ambassador variant
    case OVERVIEW_CONSTANT.AMBASSADOR_CODE[2]: // 'PPAE' - Ambassador variant
      return MEMBER_STATUS.NOT_NEAR;

    default:
      return MEMBER_STATUS.NOT_NEAR;
  }
};

/**
 * Gets the target tier for benefit display based on current status
 *
 * @param levelTypeCode - Current member level type code
 * @param memberStatus - Current member status
 * @returns Target tier code for benefit display
 *
 * @example
 * ```typescript
 * const targetTier = getTargetTierForBenefits('G', 'near-achieving');
 * console.log(targetTier); // 'P' (shows Platinum benefits)
 * ```
 */
export const getTargetTierForBenefits = (
  levelTypeCode: string | undefined,
  memberStatus: 'not-near' | 'near-renewing' | 'near-achieving'
): string => {
  // Normalize levelTypeCode to core code (handles all tier variants)
  const normalizedLevelTypeCode = normalizeTierCode(levelTypeCode);

  if (memberStatus === MEMBER_STATUS.NEAR_ACHIEVING) {
    // Show next tier benefits
    switch (normalizedLevelTypeCode) {
      case CORE_LEVEL_CODES[0]: // 'R' - Member
        return CORE_LEVEL_CODES[1]; // 'S' - Silver
      case CORE_LEVEL_CODES[1]: // 'S' - Silver
        return CORE_LEVEL_CODES[2]; // 'G' - Gold
      case CORE_LEVEL_CODES[2]: // 'G' - Gold
        return CORE_LEVEL_CODES[3]; // 'P' - Platinum
      case CORE_LEVEL_CODES[3]: // 'P' - Platinum
        return constants.TITANIUM_LEVEL_CODE; // 'PPC' - Titanium
      case constants.TITANIUM_LEVEL_CODE: // 'PPC' - Titanium
        return CORE_LEVEL_CODES[5]; // 'PFS' - Ambassador
      default:
        return normalizedLevelTypeCode || CORE_LEVEL_CODES[0]; // Default to 'R' - Member
    }
  }

  // For 'not-near' and 'near-renewing', show current tier benefits
  return normalizedLevelTypeCode || CORE_LEVEL_CODES[0]; // Default to 'R' - Member
};

/**
 * Gets the color for the benefit pill ribbon based on member status
 * For near-achieving scenarios, returns the next-tier color
 * For other scenarios, returns the current-tier color
 *
 * @param levelTypeCode - Current member level type code
 * @param memberStatus - Current member status
 * @param memberStatusList - Optional array of member status configurations
 * @returns Color string for the benefit pill ribbon
 *
 * @example
 * ```typescript
 * const ribbonColor = getBenefitRibbonColor('G', 'near-achieving', memberStatusList);
 * console.log(ribbonColor); // Returns Platinum color for Gold near-achieving member
 * ```
 */
export const getBenefitRibbonColor = (
  levelTypeCode: string | undefined,
  memberStatus: 'not-near' | 'near-renewing' | 'near-achieving',
  memberStatusList?: Array<{ id: string; label: string }>
): string => {
  // For near-achieving, show next-tier color on the benefit pill
  if (memberStatus === MEMBER_STATUS.NEAR_ACHIEVING) {
    const targetTier = getTargetTierForBenefits(levelTypeCode, memberStatus);
    const { arcColor } = getNightsProgressColors(targetTier, memberStatusList, true);
    return arcColor;
  }

  // For other statuses (not-near, near-renewing), show current-tier color
  const { arcColor } = getNightsProgressColors(levelTypeCode, memberStatusList, true);
  return arcColor;
};

/**
 * Gets the benefit rotation key for session storage
 *
 * @param targetTier - The target tier for rotation
 * @returns Session storage key for benefit rotation
 */
export const getBenefitRotationKey = (targetTier: string): string => {
  return `${BENEFIT_ROTATION.SESSION_STORAGE_PREFIX}${targetTier}`;
};

/**
 * Gets or creates a session ID for benefit rotation tracking
 * Persists across page reloads to prevent benefit flickering
 *
 * @param memberNumber - Optional member number to bind session to specific user
 * @returns The current or newly created session ID
 */
export const getOrCreateBenefitSessionId = (memberNumber?: string): string => {
  const existingSessionId = sessionStorage.getItem(BENEFIT_ROTATION.SESSION_ID_KEY);

  // If session exists and member number is provided, check if it matches
  if (existingSessionId && memberNumber) {
    if (existingSessionId.includes(`_${memberNumber}`)) {
      return existingSessionId; // Keep existing session for same member (no rotation on page reload)
    }
    // Different member - clear rotation data for fresh start
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(BENEFIT_ROTATION.SESSION_STORAGE_PREFIX) || key === BENEFIT_ROTATION.SESSION_ID_KEY) {
        sessionStorage.removeItem(key);
      }
    });
  }

  // Create new session ID (with member number if provided)
  const newSessionId = memberNumber ? `session_${Date.now()}_${memberNumber}` : `session_${Date.now()}`;
  sessionStorage.setItem(BENEFIT_ROTATION.SESSION_ID_KEY, newSessionId);
  return newSessionId;
};

/**
 * Clears all benefit rotation data for all tiers
 * Called automatically on logout via clearSessionAndCookie()
 * Ensures fresh rotation on next login
 */
export const forceBenefitRotation = (): void => {
  // Clear all benefit rotation data AND session ID to force fresh selection
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith(BENEFIT_ROTATION.SESSION_STORAGE_PREFIX) || key === BENEFIT_ROTATION.SESSION_ID_KEY) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Public helper to clear benefit rotation data - can be called from logout handlers
 * Alias for forceBenefitRotation() for better discoverability
 *
 * @example
 * ```typescript
 * // In your logout handler
 * import { clearBenefitRotationData } from '@mi-account-components';
 *
 * function handleLogout() {
 *   clearSessionAndCookie();
 *   clearBenefitRotationData(); // Clear benefit rotation
 * }
 * ```
 */
export const clearBenefitRotationData = forceBenefitRotation;

/**
 * Gets the rotated benefit index with shuffled rotation logic
 * Ensures all benefits are shown in a randomized but exhaustive manner
 *
 * @param targetTier - The target tier for rotation
 * @param totalBenefits - Total number of benefits available
 * @param memberNumber - Member number to tie session to specific user
 * @returns Index of the benefit to display
 */
export const getRotatedBenefitIndex = (targetTier: string, totalBenefits: number, memberNumber?: string): number => {
  // Validate input parameters
  if (!targetTier || totalBenefits <= 0) {
    return 0;
  }

  const key = getBenefitRotationKey(targetTier);

  // Get or create session ID to track unique sessions - pass memberNumber to tie session to user
  const currentSessionId = getOrCreateBenefitSessionId(memberNumber);

  // Get current stored data
  const storedData = sessionStorage.getItem(key);
  let shuffleQueue: number[] = [];
  let currentIndex: number | null = null;
  let sessionId = '';

  if (storedData) {
    try {
      const parsed = JSON.parse(storedData);
      shuffleQueue = parsed.queue || [];
      currentIndex = parsed.currentIndex ?? null;
      sessionId = parsed.sessionId || '';
    } catch (e) {
      // If parsing fails, start fresh
      shuffleQueue = [];
      currentIndex = null;
      sessionId = '';
    }
  }

  // If we have a current index and session hasn't changed, return it (don't pop from queue on every render)
  if (currentIndex !== null && sessionId === currentSessionId && currentIndex < totalBenefits) {
    return currentIndex;
  }

  // If queue is empty or session changed, create a new shuffled queue of all benefit indices
  if (shuffleQueue.length === 0 || sessionId !== currentSessionId) {
    // Create array of all indices [0, 1, 2, ..., totalBenefits-1]
    shuffleQueue = Array.from({ length: totalBenefits }, (_, i) => i);

    // Fisher-Yates shuffle algorithm for true randomization
    for (let i = shuffleQueue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffleQueue[i], shuffleQueue[j]] = [shuffleQueue[j], shuffleQueue[i]];
    }
  }

  // Pop the next benefit from the queue
  const nextIndex = shuffleQueue.shift() || 0;

  // Store the updated queue, current index, and session ID
  const dataToStore = {
    queue: shuffleQueue,
    currentIndex: nextIndex,
    sessionId: currentSessionId,
  };
  sessionStorage.setItem(key, JSON.stringify(dataToStore));

  // Ensure index is within bounds
  return Math.max(0, Math.min(nextIndex, totalBenefits - 1));
};

/**
 * Gets benefit content configuration based on target tier using AEM data
 *
 * @param targetTier - The target tier for benefit display
 * @param aemModel - AEM model data containing benefit content
 * @returns Benefit configuration from AEM
 */
export const getBenefitContent = (
  targetTier: string,
  aemModel?: AemModel,
  memberStatusInfo?: { nightsToTarget: number; status: string },
  isUSLocation?: boolean,
  isCALocation?: boolean,
  memberNumber?: string | number
): BenefitData | null => {
  // Only use AEM data - no fallbacks
  if (aemModel?.['miniBannerDetails']) {
    // Find the appropriate benefit from AEM miniBannerDetails with rotation
    const benefitIndex = getBenefitIndexForTier(targetTier, aemModel, isUSLocation, isCALocation, memberNumber);

    // Return null if invalid tier or index out of bounds
    if (benefitIndex < 0 || benefitIndex >= aemModel['miniBannerDetails'].length) {
      return null;
    }

    const benefit = aemModel['miniBannerDetails'][benefitIndex];

    if (benefit) {
      // Use the existing memberStatusInfo that already has the correct nightsToTarget calculated
      const nightsToTarget = memberStatusInfo?.nightsToTarget || 0;
      const memberStatus = memberStatusInfo?.status;

      // Get the appropriate description template directly from AEM model
      let descriptionTemplate = '';

      switch (memberStatus) {
        case MEMBER_STATUS.NOT_NEAR:
          // Use AEM data directly - no fallbacks
          descriptionTemplate = aemModel[BENEFIT_TEMPLATES.NOT_NEAR] || '';
          break;

        case MEMBER_STATUS.NEAR_RENEWING:
          // Use AEM data directly - no fallbacks
          descriptionTemplate = aemModel[BENEFIT_TEMPLATES.NEAR_RENEWING] || '';
          break;

        case MEMBER_STATUS.NEAR_ACHIEVING:
          // Use AEM data directly - no fallbacks
          descriptionTemplate = aemModel[BENEFIT_TEMPLATES.NEAR_ACHIEVING] || '';
          break;

        default:
          descriptionTemplate = benefit.benefitDescription || '';
      }

      // Handle dynamic description based on member status and nightsToTarget
      let processedDescription = descriptionTemplate;

      // Replace {0} placeholder with actual nightsToTarget number
      if (descriptionTemplate && descriptionTemplate.includes('{0}')) {
        // AC1 (WEB-195730): Titanium near-achieving Ambassador with nights met but spend not met.
        // Use dedicated AEM field instead of contradictory "Enjoy this exclusive benefit!"
        if (memberStatus === MEMBER_STATUS.NEAR_ACHIEVING && nightsToTarget === 0) {
          processedDescription =
            (aemModel?.[BENEFIT_TEMPLATES.NIGHTS_MET_SPEND_NOT_MET] as string) || '';
        } else {
          // Normal case: Replace placeholders with actual values
          processedDescription = replaceText(descriptionTemplate, [
            nightsToTarget.toString(),
            nightsToTarget === 1 ? aemModel?.nightLabel || '' : aemModel?.nightsLabel || '',
          ]);
        }
      }

      // For "Near Achieving" scenarios, show locked icon if the benefit is for next tier
      const isLocked = memberStatus === MEMBER_STATUS.NEAR_ACHIEVING;

      const result = {
        icon: isLocked ? 'icon-lock' : extractIconClass(benefit.benefitIcon),
        header: benefit.benefitHeader || '',
        subtitle: processedDescription || '',
      };

      return result;
    }
  }

  // Return null if no AEM data available - no fallbacks
  return null;
};

/**
 * Gets the benefit index for a given tier from AEM miniBannerDetails with rotation
 */
const getBenefitIndexForTier = (
  targetTier: string,
  aemModel?: AemModel,
  isUSLocation?: boolean,
  isCALocation?: boolean,
  memberNumber?: string | number // Added memberNumber parameter
): number => {
  if (!aemModel?.['miniBannerDetails']) {
    return -1;
  }

  // Find all benefits for this tier using the 'key' field
  let tierBenefits = aemModel['miniBannerDetails'].filter(benefit => benefit.key === targetTier);

  // Apply location filtering based on locationRestriction
  // CRITICAL: Always filter benefits based on location restrictions
  tierBenefits = tierBenefits.filter(benefit => {
    const locationRestriction = benefit.locationRestriction;

    // If no location restriction or null, show the benefit globally
    if (!locationRestriction || locationRestriction === LOCATION_RESTRICTIONS.NULL) {
      return true;
    }

    // Check location restrictions based on AEM data pattern
    // Support both 'US/CA' and 'US_CA' formats from backend
    if (
      locationRestriction === LOCATION_RESTRICTIONS.US_CA_SLASH ||
      locationRestriction === LOCATION_RESTRICTIONS.US_CA_UNDERSCORE
    ) {
      // Only show to US or CA users (explicitly true, not undefined/false)
      return isUSLocation === true || isCALocation === true;
    } else if (locationRestriction === LOCATION_RESTRICTIONS.US) {
      // Only show to US users (explicitly true)
      return isUSLocation === true;
    } else if (locationRestriction === LOCATION_RESTRICTIONS.CA) {
      // Only show to CA users (explicitly true)
      return isCALocation === true;
    }

    // For any other restrictions, hide the benefit (safe default)
    return false;
  });

  if (tierBenefits.length === 0) return -1;

  // Implement rotation logic using session storage
  const rotatedIndex = getRotatedBenefitIndex(
    targetTier,
    tierBenefits.length,
    memberNumber ? String(memberNumber) : undefined
  );

  // Find the actual index in the full array
  const actualIndex = aemModel['miniBannerDetails'].findIndex(benefit => benefit === tierBenefits[rotatedIndex]);

  return actualIndex;
};

/**
 * Enhanced helper function for MemberBenefitsV2 component with dynamic benefit selection
 *
 * @param levelTypeCode - Member's level type code
 * @param memberLevel - Member's level code
 * @param totalNights - Total nights stayed (optional)
 * @param totalSpend - Total spend (optional)
 * @param isUSLocation - US location flag for benefit filtering
 * @param isCALocation - CA location flag for benefit filtering
 * @param aemModel - AEM model data containing benefit content (optional)
 * @returns Complete member benefit data including colors, status, and target tier
 *
 * @example
 * ```typescript
 * const benefitData = getMemberBenefitData('G', 'G', 45, 15000, false, false, aemModel);
 * console.log(benefitData.targetTier); // 'P' for near-achieving Gold member
 * ```
 */
export const getMemberBenefitData = (
  levelTypeCode: string | undefined,
  memberLevel: string | undefined,
  totalNights?: number,
  totalSpend?: number,
  isUSLocation?: boolean,
  isCALocation?: boolean,
  aemModel?: AemModel,
  uxlData?: Record<string, unknown>,
  memberStatusList?: Array<{ id: string; label: string }>,
  memberNumber?: string | number // Added memberNumber parameter
): MemberBenefitResult => {
  // For basic members (R), use memberLevel instead of levelTypeCode for colors
  const colorCode = levelTypeCode === CORE_LEVEL_CODES[0] ? memberLevel : levelTypeCode; // Use memberLevel for 'R' - Member

  // Use the same approach as the badge icon and progress meter
  // Pass memberStatusList to ensure we use the same color resolution as badge icon
  const { arcColor } = getNightsProgressColors(levelTypeCode, memberStatusList);
  const iconColor = arcColor;

  // Use the existing determineMemberStatus function to get the full MemberStatusInfo object
  // Transform AEM data to the structure expected by determineMemberStatus
  let memberLevelBenefits: Array<{
    key: string;
    notNearTitle: string;
    notNearSubTitle: string;
    nearRenewingMinValue?: string;
    nearRenewingMaxValue?: string;
    nearAchievingMinValue?: string;
    nearAchievingMaxValue?: string;
  }> = [];

  // Check both possible AEM property names (membershipbenefits or membershipbenefits_c)
  const aemWithAlternateKey = aemModel as AemModel & {
    membershipbenefits_c?: { memberLevelBenefits?: BenefitConfig[] };
  };
  const memberLevelBenefitsData =
    aemModel?.membershipbenefits?.memberLevelBenefits || aemWithAlternateKey?.membershipbenefits_c?.memberLevelBenefits;

  if (memberLevelBenefitsData && memberLevelBenefitsData.length > 0) {
    memberLevelBenefits = memberLevelBenefitsData.map((benefit: BenefitConfig) => ({
      key: benefit.key || normalizeTierCode(levelTypeCode),
      notNearTitle: benefit.notNearTitle || benefit.heading || 'Elite Benefits',
      notNearSubTitle: benefit.notNearSubTitle || benefit.subHeading || BENEFIT_TEMPLATES.NOT_NEAR,
      nearRenewingMinValue: benefit.nearRenewingMinValue,
      nearRenewingMaxValue: benefit.nearRenewingMaxValue,
      nearAchievingMinValue: benefit.nearAchievingMinValue,
      nearAchievingMaxValue: benefit.nearAchievingMaxValue,
      hundredPlus: benefit.hundredPlus,
    }));
  } else {
    // Fallback: Create basic memberLevelBenefits structure when AEM data is not available
    // This ensures the determineMemberStatus function can still work
    memberLevelBenefits = [
      {
        key: normalizeTierCode(levelTypeCode),
        notNearTitle: 'Elite Benefits',
        notNearSubTitle: BENEFIT_TEMPLATES.NOT_NEAR,
        nearRenewingMinValue: '7',
        nearRenewingMaxValue: '9',
        nearAchievingMinValue: '18',
        nearAchievingMaxValue: '24',
      },
    ];
  }

  // Use actual UXL data - no fallbacks in production
  if (!uxlData) {
    return {
      colorCode: levelTypeCode || '',
      iconColor: '',
      benefitData: null,
      memberStatus: 'not-near',
      targetTier: '',
    };
  }

  // Get lifetime status from UXL data
  const uxlLoyaltyInfo = uxlData as {
    loyaltyInformation?: { rewards?: { eliteLifetimeLevel?: { code?: string } } };
  };
  const lifetimeStatus = uxlLoyaltyInfo?.loyaltyInformation?.rewards?.eliteLifetimeLevel?.code || null;

  // Use getMemberStatus directly for MiniBanner (simpler, no AEM config dependency)
  const memberStatus = getMemberStatus(levelTypeCode, totalNights || 0, totalSpend || 0, lifetimeStatus);
  const targetTier = getTargetTierForBenefits(levelTypeCode, memberStatus);

  // Calculate nightsToTarget for the subtitle
  const nightsRequired = EARNINGS_SUMMARY_CONSTANTS.TIER_NIGHT_REQUIREMENTS[targetTier] || 0;
  const nightsToTarget = Math.max(0, nightsRequired - (totalNights || 0));

  const benefitData = getBenefitContent(
    targetTier,
    aemModel,
    { nightsToTarget, status: memberStatus },
    isUSLocation,
    isCALocation,
    memberNumber
  );

  // Get ribbon color - for near-achieving, use next-tier color
  const ribbonColor = getBenefitRibbonColor(levelTypeCode, memberStatus, memberStatusList);

  return {
    colorCode,
    iconColor,
    benefitData,
    memberStatus,
    targetTier,
    ribbonColor,
  };
};
